import { useEffect, useRef, useState } from "react";

/**
 * Returns the `id` of the top (newest) record in a chronologically-sorted
 * list ONLY when it changes from the previously-observed top — i.e. when a
 * save flow refetches the list and a new record surfaces at the top.
 *
 * Callers add the returned id as a class/testID hook so a brief CSS
 * animation highlights the row just landed. Does not fire on first mount,
 * on list order changes not caused by a new top record, or on stale
 * re-renders.
 */
export function useNewestRecordFlash(sortedRecords, flashMs = 2000) {
  const [highlightId, setHighlightId] = useState(null);
  const prevFirstIdRef = useRef(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    const firstId = sortedRecords[0]?.id ?? null;
    if (!initializedRef.current) {
      prevFirstIdRef.current = firstId;
      initializedRef.current = true;
      return undefined;
    }
    if (firstId && firstId !== prevFirstIdRef.current) {
      prevFirstIdRef.current = firstId;
      setHighlightId(firstId);
      const timer = setTimeout(() => setHighlightId(null), flashMs);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [sortedRecords, flashMs]);

  return highlightId;
}
